/**
 * Pure pricing helpers usati da OrderEditor.jsx.
 *
 * Tenuti out-of-component perché:
 *  - sono pure functions (deterministiche, no React state)
 *  - facilitano i test unitari (es. `calcLineTotal({qty,...}, 5) === 1.43`)
 *  - riducono la complessità del file OrderEditor
 */

/** Tolleranza usata nell'arrotondamento per neutralizzare i piccoli errori
 *  di rappresentazione IEEE 754 (es. 1.425 = 1.4249999..., 4.275 = 4.2749999...).
 *  Valore scelto: 1e-8 (≈ 0,00000001) — abbondante per stare ben dentro la
 *  precisione delle cifre monetarie a 2 decimali. */
const ROUND_EPS = 1e-8;

/** Totale di una riga: qty * price * (1-sc1%) * ... * (1-canale%) — arrotondato a 2 decimali.
 *  L'aggiunta di `ROUND_EPS` previene il classico off-by-1-cent dovuto
 *  alla rappresentazione IEEE 754 dei decimali (es. 1.425 → 1.43, 4.275 → 4.28). */
export function calcLineTotal(item, scontoCanale = 0) {
  const qty = parseFloat(item.quantity) || 0;
  const price = parseFloat(item.price_unit) || 0;
  let total = qty * price;
  for (const s of [item.sc1, item.sc2, item.sc3, item.sc4]) {
    const v = parseFloat(s) || 0;
    if (v > 0) total *= (1 - v / 100);
  }
  if (scontoCanale > 0) total *= (1 - scontoCanale / 100);
  return Math.round((total + ROUND_EPS) * 100) / 100;
}

/** Netto unitario: price * (1-sc1%) * ... * (1-canale%) — non arrotondato */
export function calcNettoUnit(item, scontoCanale = 0) {
  const price = parseFloat(item.price_unit) || 0;
  let v = price;
  for (const s of [item.sc1, item.sc2, item.sc3, item.sc4]) {
    const x = parseFloat(s) || 0;
    if (x > 0) v *= (1 - x / 100);
  }
  if (scontoCanale > 0) v *= (1 - scontoCanale / 100);
  return v;
}

/**
 * Indica se la riga di un articolo NON Triolo è "senza prezzo deciso" e
 * quindi rischia di essere fatturata al listino con perdita di marginalità.
 *
 * Regole (stessa logica della pagina Giri):
 *  - clientScontiFissi (cliente contrattualizzato) → mai warning
 *  - prodotto Triolo → mai warning
 *  - sc1+sc2+sc3+sc4 > 0 → sconto applicato, OK
 *  - ultimo_prezzo del cliente >= netto base (listino × canale) → margine pieno, OK
 *  - altrimenti → warning
 */
export function needsPriceWarning(item, { products, lastPrices, scontoCanale, clientScontiFissi }) {
  if (clientScontiFissi) return false;
  // Solo articoli con qta vendita > 0: gli omaggi puri non richiedono
  // conferma prezzo (non vengono fatturati).
  if (!((Number(item.quantity) || 0) > 0)) return false;
  const prod = products.find((p) => p.id === item.product_id);
  if (!prod) return false;
  const isTriolo = String(prod.macro_category || "").trim().toLowerCase() === "prodotti triolo";
  if (isTriolo) return false;
  const scSum =
    (Number(item.sc1) || 0) +
    (Number(item.sc2) || 0) +
    (Number(item.sc3) || 0) +
    (Number(item.sc4) || 0);
  if (scSum > 0) return false;
  const ultimo = Number(lastPrices[item.product_id]?.ultimo_prezzo || 0);
  const priceList = Number(item.price_unit) || 0;
  const nettoBase = priceList * (scontoCanale > 0 ? (1 - scontoCanale / 100) : 1);
  const tolerance = Math.max(0.05, nettoBase * 0.005);
  if (ultimo > 0 && ultimo >= nettoBase - tolerance) return false;
  return true;
}

/** Netto unitario di un singolo prodotto in fase di selezione (pendingPrice + sconti manuali) */
export function calcPendingNetto({ pendingPrice, pendingSc1, pendingSc2, pendingSc3, pendingSc4 }, scontoCanale = 0) {
  const price = parseFloat(pendingPrice) || 0;
  let v = price;
  for (const s of [pendingSc1, pendingSc2, pendingSc3, pendingSc4]) {
    const x = parseFloat(s) || 0;
    if (x > 0) v *= (1 - x / 100);
  }
  if (scontoCanale > 0) v *= (1 - scontoCanale / 100);
  return v;
}
